import { useCallback, useRef, useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../components/session/postSessionCard';
import { ContentCard } from '../../../components/common/ContentCard';
import { CheckCircle, Edit3, Lock } from 'react-feather';
import {
  RichTextEditor,
  LINK_CLICKABILITY_HELPER_TEXT,
  type RichTextControl,
} from '../../../components/common/RichTextEditor/RichTextEditor';
import { participantPostSessionApi } from '../../../services/api/participantPostSession';

const NOTES_AUTOSAVE_DEBOUNCE_MS = 800;

// MED-1014 reduced toolbar — mirrors the coach Session Notes editor (MED-1050).
const NOTES_TOOLBAR: RichTextControl[][] = [
  ['bold', 'italic', 'underline'],
  ['bulletList', 'orderedList'],
  ['link'],
];

const TITLE_COLOR = '#1A1A2E';
const SUBTITLE_COLOR = '#9CA3AF';
const NOTES_BG = '#F9FAFB';
const NOTES_BORDER = '#E5E7EB';
const NOTES_TEXT = '#374151';
// MED-1057 — "Edit Notes" secondary text link (Figma teal).
const EDIT_LINK_COLOR = '#2D7B77';

interface Props {
  sessionId: string;
  initialNotesHtml: string;
  reviewed: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  /** Called after the participant clicks "Mark as Reviewed" (Step 2 collapse). */
  onMarkReviewed: () => void;
}

/**
 * Step 2 — Review Session Notes card (MED-806 AC8-AC10; polished by MED-1057).
 *
 * The participant counterpart to the coach Session Notes card (MED-1050) — the
 * stripped-down, no-AI version. Notes are pre-filled from the participant's
 * in-session private notes (E3.3) and autosave to `session_participant_notes`
 * via the existing MED-805 PUT endpoint.
 *
 * MED-1057 polish:
 *   - Read-mode-first: shows a clean read-only display of the notes with an
 *     "Edit Notes" text link; clicking it opens the reduced-toolbar editor
 *     (no toggle back — AC4).
 *   - Header carries the step name ("Review Session Notes", no in-card number)
 *     + the "Only you can see this" privacy label, in both modes.
 *   - "Mark as Reviewed" stays the primary completion CTA (bottom-right, in
 *     both modes); "Edit Notes" is a secondary link to its left.
 *   - No AI affordances (Clean Up / Add more are coach-only → MED-893).
 */
export function ReviewNotesCard({
  sessionId,
  initialNotesHtml,
  reviewed,
  expanded,
  onToggleExpand,
  onMarkReviewed,
}: Props) {
  const [notesHtml, setNotesHtml] = useState(initialNotesHtml);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleNotesChange = useCallback(
    (html: string) => {
      setNotesHtml(html);
      setJustSaved(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        setSaving(true);
        try {
          await participantPostSessionApi.upsertNotes(sessionId, html);
          setJustSaved(true);
        } catch {
          // Autosave failure is non-blocking; the next keystroke retries.
        } finally {
          setSaving(false);
        }
      }, NOTES_AUTOSAVE_DEBOUNCE_MS);
    },
    [sessionId]
  );

  const handleMarkReviewed = async () => {
    setMarkingReviewed(true);
    try {
      await participantPostSessionApi.markNotesReviewed(sessionId);
      onMarkReviewed();
    } finally {
      setMarkingReviewed(false);
    }
  };

  const hasNotes = Boolean(
    notesHtml && notesHtml.trim() !== '' && notesHtml.trim() !== '<p></p>'
  );

  const markReviewedButton = !reviewed && (
    <Button
      size="sm"
      onClick={handleMarkReviewed}
      loading={markingReviewed}
      data-testid="notes-mark-reviewed"
      styles={{
        root: { backgroundColor: POST_SESSION_PRIMARY_BG, color: '#FFFFFF' },
      }}
    >
      Mark as Reviewed
    </Button>
  );

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      data-testid="card-review-notes"
      data-expanded={expanded ? 'true' : 'false'}
      data-reviewed={reviewed ? 'true' : 'false'}
      header={
        <UnstyledButton
          onClick={onToggleExpand}
          style={{ width: '100%' }}
          aria-expanded={expanded}
          data-testid="card-review-notes-header"
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Group gap={8} align="center" wrap="nowrap">
                <Edit3
                  size={18}
                  color={TITLE_COLOR}
                  aria-hidden="true"
                  style={{ marginTop: -2 }}
                />
                <Text fz={15} fw={700} c={TITLE_COLOR}>
                  Review Session Notes
                </Text>
                {reviewed && (
                  <Group gap={4} c="medleyGreen">
                    <CheckCircle size={14} />
                    <Text size="xs" fw={600} tt="uppercase">
                      Reviewed
                    </Text>
                  </Group>
                )}
              </Group>
              {/* AC3 — read-mode affordance copy, sitting just under the title to
                match the sibling cards' header subtitle spacing. */}
              {expanded && !editing && (
                <Text fz={12} fw={500} c={SUBTITLE_COLOR}>
                  What you captured during the session. Click{' '}
                  <Text span inherit fw={700} c={NOTES_TEXT}>
                    Edit Notes
                  </Text>{' '}
                  to keep refining.
                </Text>
              )}
            </Stack>
            <Group gap={4} c={SUBTITLE_COLOR} wrap="nowrap">
              <Lock size={14} />
              <Text fz={12}>Only you can see this</Text>
            </Group>
          </Group>
        </UnstyledButton>
      }
    >
      {expanded && (
        <Stack gap="md">
          {editing ? (
            <>
              <RichTextEditor
                value={notesHtml}
                onChange={handleNotesChange}
                toolbar={NOTES_TOOLBAR}
                placeholder="Add anything that came up after the session…"
                minHeight={220}
                helperText={LINK_CLICKABILITY_HELPER_TEXT}
              />
              <Group justify="space-between" align="center" wrap="nowrap">
                {/* Stable left slot so "Mark as Reviewed" stays right-aligned
                    even while the SaveStatus indicator is idle (renders null). */}
                <Box>
                  <SaveStatus saving={saving} saved={justSaved} />
                </Box>
                {markReviewedButton}
              </Group>
            </>
          ) : (
            <>
              {/* AC3 — read-mode read-only display of the notes. */}
              <Box
                style={{
                  backgroundColor: NOTES_BG,
                  border: `1px solid ${NOTES_BORDER}`,
                  borderRadius: 10,
                  padding: '21px 17px 15px',
                  minHeight: 80,
                }}
              >
                {hasNotes ? (
                  <Box
                    style={{
                      color: NOTES_TEXT,
                      fontSize: 13,
                      lineHeight: 1.65,
                    }}
                    dangerouslySetInnerHTML={{ __html: notesHtml }}
                  />
                ) : (
                  <Text c="dimmed" fs="italic" fz={13}>
                    No session notes were captured. Click Edit Notes to start
                    writing.
                  </Text>
                )}
              </Box>
              <Group justify="flex-end" gap="md" align="center">
                <Anchor
                  component="button"
                  type="button"
                  onClick={() => setEditing(true)}
                  fz={13}
                  fw={500}
                  c={EDIT_LINK_COLOR}
                  data-testid="notes-edit"
                >
                  Edit Notes
                </Anchor>
                {markReviewedButton}
              </Group>
            </>
          )}
        </Stack>
      )}
    </ContentCard>
  );
}

/**
 * MED-1057 AC8 — card-level Saving… / Saved status (no timestamp), matching the
 * coach Session Notes treatment: muted "Saving…" while an autosave is in
 * flight, then a green-dot "Saved".
 */
function SaveStatus({ saving, saved }: { saving: boolean; saved: boolean }) {
  if (saving) {
    return (
      <Text size="xs" c="dimmed">
        Saving…
      </Text>
    );
  }
  if (saved) {
    return (
      <Group gap={6} align="center" wrap="nowrap">
        <Box
          w={8}
          h={8}
          style={{
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-medleyGreen-6)',
          }}
        />
        <Text size="xs" c="dimmed">
          Saved
        </Text>
      </Group>
    );
  }
  return null;
}

export default ReviewNotesCard;
